import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { enquiriesApi } from '../../services/smartBillApi';
import './Enquiries.css';

const STATUS_OPTIONS = [
  { value: 'interested', label: 'Still Interested', color: '#10b981', icon: 'fas fa-heart' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444', icon: 'fas fa-times-circle' },
  { value: 'converted', label: 'Converted', color: '#6366f1', icon: 'fas fa-check-circle' },
];

const SOURCE_SEGMENTS = [
  { value: 'new', label: 'New', apiSource: 'standalone_form' },
  { value: 'old', label: 'Old', apiSource: 'smart_bill' },
  { value: 'demo', label: 'Demo', apiSource: 'demo' },
];

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return '—';
  }
};

const formatDateOnly = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    return '—';
  }
};

const safeParseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const Enquiries = ({
  embedded = false,
  defaultSourceSegment = 'new',
  hideSourceChips = false,
} = {}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSegment = (() => {
    if (hideSourceChips && defaultSourceSegment === 'demo') return 'demo';
    const fromUrl = (searchParams.get('source') || '').toLowerCase();
    if (fromUrl === 'old' || fromUrl === 'smart_bill') return 'old';
    if (fromUrl === 'demo') return 'demo';
    if (fromUrl === 'new' || fromUrl === 'standalone_form') return 'new';
    return defaultSourceSegment === 'old'
      ? 'old'
      : defaultSourceSegment === 'demo'
        ? 'demo'
        : 'new';
  })();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [editForm, setEditForm] = useState({
    person_name: '',
    phone: '',
    work_purpose: '',
    visited_date: '',
    status: 'interested',
    remarks: '',
    address: '',
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceSegment, setSourceSegment] = useState(initialSegment);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingEnquiry, setConvertingEnquiry] = useState(null);

  useEffect(() => {
    if (hideSourceChips && defaultSourceSegment === 'demo' && sourceSegment !== 'demo') {
      setSourceSegment('demo');
    }
  }, [hideSourceChips, defaultSourceSegment, sourceSegment]);

  const handleSourceSegmentChange = (segmentValue) => {
    setSourceSegment(segmentValue);
    if (!embedded) return;
    const next = new URLSearchParams(searchParams);
    next.set('source', segmentValue);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    fetchEnquiries();
  }, [filterStatus, searchTerm, sourceSegment]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const segment = SOURCE_SEGMENTS.find((s) => s.value === sourceSegment) || SOURCE_SEGMENTS[0];
      const params = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        search: searchTerm || undefined,
        source: segment.apiSource,
        limit: 10000,
      };

      const response = await enquiriesApi.getAll(params);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch enquiries');
      }

      const sortedEnquiries = (response.data || []).sort((a, b) => {
        const dateA = new Date(a.visited_date || a.created_at || 0);
        const dateB = new Date(b.visited_date || b.created_at || 0);
        return dateB - dateA;
      });

      setEnquiries(sortedEnquiries);
    } catch (err) {
      console.error('Failed to load enquiries:', err);
      toast.error(err.message || 'Failed to load enquiries');
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    const billData = enquiry.bill_data || {};
    const customerData = billData.customerData || {};
    setEditForm({
      person_name: enquiry.person_name || '',
      phone: enquiry.phone || '',
      work_purpose: enquiry.work_purpose || '',
      visited_date: enquiry.visited_date
        ? new Date(enquiry.visited_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      status: enquiry.status || 'interested',
      remarks: enquiry.remarks || '',
      address: customerData.address || '',
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdate = async () => {
    if (!editingEnquiry) return;

    try {
      setLoading(true);
      // Update bill_data with address if it exists
      const updateData = { ...editForm };
      if (editingEnquiry.bill_data) {
        updateData.bill_data = {
          ...editingEnquiry.bill_data,
          customerData: {
            ...(editingEnquiry.bill_data.customerData || {}),
            address: editForm.address || ''
          }
        };
      }
      await enquiriesApi.update(editingEnquiry.id, updateData);

      toast.success('Enquiry updated successfully!');
      setEditingEnquiry(null);
      setEditForm({
        person_name: '',
        phone: '',
        work_purpose: '',
        visited_date: '',
        status: 'interested',
        remarks: '',
        address: '',
      });
      await fetchEnquiries();
    } catch (err) {
      console.error('Failed to update enquiry:', err);
      toast.error(err.message || 'Failed to update enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (enquiry) => {
    if (!window.confirm(`Are you sure you want to delete enquiry for ${enquiry.person_name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await enquiriesApi.delete(enquiry.id);
      toast.success('Enquiry deleted successfully!');
      await fetchEnquiries();
    } catch (err) {
      console.error('Failed to delete enquiry:', err);
      toast.error(err.message || 'Failed to delete enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = (enquiry) => {
    if (!enquiry.phone) {
      toast.error('Phone number is required');
      return;
    }

    const phone = enquiry.phone.replace(/\D/g, '');
    if (phone.length !== 10) {
      toast.error('Invalid phone number');
      return;
    }

    const statusLabel = STATUS_OPTIONS.find((opt) => opt.value === enquiry.status)?.label || enquiry.status;
    const servicesList = enquiry.services && Array.isArray(enquiry.services) && enquiry.services.length > 0
      ? enquiry.services.map((s) => `• ${s.display_name || s.name || s.service_name || 'N/A'}`).join('\n')
      : 'N/A';

    const message = `*Hello ${enquiry.person_name}!*\n\n` +
      `Thank you for visiting us on ${formatDateOnly(enquiry.visited_date)}.\n\n` +
      `*Work Purpose:* ${enquiry.work_purpose || 'General Enquiry'}\n` +
      `${servicesList !== 'N/A' ? `*Services:*\n${servicesList}\n` : ''}` +
      `${enquiry.total_amount > 0 ? `*Estimated Amount:* ${formatCurrency(enquiry.total_amount)}\n` : ''}` +
      `*Status:* ${statusLabel}\n` +
      `${enquiry.remarks ? `*Remarks:* ${enquiry.remarks}\n\n` : '\n'}` +
      `We're here to help! Feel free to reach out if you have any questions. 🙏\n\n` +
      `Looking forward to serving you!`;

    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCall = (enquiry) => {
    if (!enquiry.phone) {
      toast.error('Phone number is required');
      return;
    }

    const phone = enquiry.phone.replace(/\D/g, '');
    if (phone.length !== 10) {
      toast.error('Invalid phone number');
      return;
    }

    window.location.href = `tel:+91${phone}`;
  };

  const handleConvert = (enquiry) => {
    setConvertingEnquiry(enquiry);
    setShowConvertModal(true);
  };

  const confirmConvert = async () => {
    if (!convertingEnquiry) return;

    try {
      const enquiry = convertingEnquiry;
      setLoading(true);
      const response = await enquiriesApi.convert(enquiry.id);

      if (response.success) {
        toast.success('Enquiry marked as converted! Redirecting to create bill...');
        setShowConvertModal(false);
        setConvertingEnquiry(null);

        // Extract enquiry data and prepare bill data
        const billDataObj = safeParseJson(enquiry.bill_data, {});
        const customerDataObj = billDataObj.customerData || {};
        const enquiryServices = Array.isArray(enquiry.services)
          ? enquiry.services
          : safeParseJson(enquiry.services, []);

        // Prepare customer data with all available fields
        const customerData = {
          name: enquiry.person_name || customerDataObj.name || '',
          phone: enquiry.phone || customerDataObj.phone || '',
          profession: customerDataObj.profession || '',
          address: customerDataObj.address || enquiry.address || '',
          city: customerDataObj.city || '',
          pincode: customerDataObj.pincode || '',
          remarks: enquiry.remarks || customerDataObj.remarks || enquiry.work_purpose || '',
        };

        // Format services array to match SmartBillWithQR expected format
        let selectedServices = [];
        if (enquiryServices.length > 0) {
          selectedServices = enquiryServices.map((service, index) => {
            // Handle different service object structures
            const serviceName = service.display_name || service.name || service.service_name || '';
            const servicePrice = Number(service.price || service.service_charge || service.total_amount || 0);
            const quantity = Number(service.quantity || 1);
            const challanaAmount = Number(service.challana || service.challana_amount || 0);
            const total = servicePrice * quantity;

            return {
              id: `row-${Date.now()}-${index}-${Math.random().toString(36).slice(-5)}`,
              name: serviceName,
              displayName: serviceName,
              quantity: quantity,
              serviceCharge: servicePrice,
              challanaAmount: challanaAmount,
              total: total,
              price: servicePrice,
              sourceServiceId: service.id || service.service_id || null,
              mainCategory: service.main_category || service.mainCategory || '',
              subCategory: service.sub_category || service.subCategory || '',
            };
          });
        } else if (enquiry.work_purpose) {
          // If no services array but work_purpose exists, create a service from it
          const workPurpose = enquiry.work_purpose;
          const estimatedAmount = Number(enquiry.total_amount || 0);
          selectedServices = [{
            id: `row-${Date.now()}-0-${Math.random().toString(36).slice(-5)}`,
            name: workPurpose,
            displayName: workPurpose,
            quantity: 1,
            serviceCharge: estimatedAmount,
            challanaAmount: 0,
            total: estimatedAmount,
            price: estimatedAmount,
            mainCategory: '',
            subCategory: '',
          }];
        }

        // If no services at all, create an empty service row
        if (selectedServices.length === 0) {
          selectedServices = [{
            id: `row-${Date.now()}-0-${Math.random().toString(36).slice(-5)}`,
            name: '',
            displayName: '',
            quantity: 1,
            serviceCharge: 0,
            challanaAmount: 0,
            total: 0,
            price: 0,
            mainCategory: '',
            subCategory: '',
          }];
        }

        // Prepare payment data if total amount is available
        const paymentData = enquiry.total_amount > 0 ? {
          methods: {
            cash: 0,
            upi: 0,
            card: 0,
            aeps: 0,
            credit: 0,
          },
          totalPaid: 0,
          change: 0,
        } : undefined;

        // Create bill data object
        const billData = {
          customerData: customerData,
          selectedServices: selectedServices,
          sourceEnquiryId: enquiry.id,
          ...(paymentData && { paymentData: paymentData }),
        };

        // Store in sessionStorage for SmartBillWithQR to pick up
        sessionStorage.setItem('enquiryBillData', JSON.stringify(billData));
        navigate(`/bills/create?from=enquiry&id=${enquiry.id}`);
      } else {
        throw new Error(response.message || 'Failed to convert enquiry');
      }
    } catch (err) {
      console.error('Failed to convert enquiry:', err);
      toast.error(err.message || 'Failed to convert enquiry');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((opt) => opt.value === status);
    if (!option) return null;

    return (
      <span className="status-badge" style={{ backgroundColor: `${option.color}20`, color: option.color }}>
        <i className={option.icon}></i> {option.label}
      </span>
    );
  };

  const getStatusCount = (status) => {
    return enquiries.filter((e) => e.status === status).length;
  };

  return (
    <div className="enquiries-page">
      {!embedded && (
        <header className="enquiries-header">
          <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1>Enquiries Management</h1>
              <p>
                {sourceSegment === 'new'
                  ? 'Registered enquiries — call / WhatsApp when product or service is available.'
                  : sourceSegment === 'demo'
                    ? 'Book Demo requests from loksy.in/demo — call to schedule a walkthrough.'
                    : 'Old enquiries saved from Smart Bill.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/bills/enquiry-form')}
              >
                <i className="fas fa-plus"></i> Enquiry Form
              </button>
              <button className="btn btn-primary" onClick={fetchEnquiries} disabled={loading}>
                <i className="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </header>
      )}

      <div className="enquiries-filters">
        {!hideSourceChips && (
          <div className="filter-group">
            <label>Enquiry type</label>
            <div className="status-filter-chips">
              {SOURCE_SEGMENTS.map((segment) => (
                <button
                  key={segment.value}
                  type="button"
                  className={`status-chip ${sourceSegment === segment.value ? 'active' : ''}`}
                  onClick={() => handleSourceSegmentChange(segment.value)}
                >
                  {segment.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {hideSourceChips && sourceSegment === 'demo' && (
          <div className="filter-group" style={{ flex: '1 1 100%' }}>
            <p style={{ margin: 0, color: '#4b5563', fontSize: 14 }}>
              Book Demo requests from loksy.in/demo — call or WhatsApp to schedule a walkthrough.
            </p>
          </div>
        )}
        <div className="filter-group">
          <label>Filter by Status</label>
          <div className="status-filter-chips">
            <button
              className={`status-chip ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All
            </button>
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status.value}
                className={`status-chip ${filterStatus === status.value ? 'active' : ''}`}
                onClick={() => setFilterStatus(status.value)}
                style={{
                  borderColor: filterStatus === status.value ? status.color : '#d1d5db',
                  backgroundColor: filterStatus === status.value ? `${status.color}15` : '#fff',
                  color: filterStatus === status.value ? status.color : '#4b5563',
                }}
              >
                <i className={status.icon}></i> {status.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by name, phone, or work purpose..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="enquiries-summary">
        <div className="summary-card">
          <span className="summary-label">
            {sourceSegment === 'demo' ? 'Total Demo Requests' : 'Total Enquiries'}
          </span>
          <span className="summary-value">{enquiries.length}</span>
        </div>
        {STATUS_OPTIONS.map((status) => {
          const count = getStatusCount(status.value);
          return (
            <div key={status.value} className="summary-card">
              <span className="summary-label">{status.label}</span>
              <span className="summary-value" style={{ color: status.color }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {loading && enquiries.length === 0 ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{sourceSegment === 'demo' ? 'Loading demo requests...' : 'Loading enquiries...'}</p>
        </div>
      ) : enquiries.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>{sourceSegment === 'demo' ? 'No demo requests found' : 'No enquiries found'}</h3>
          <p>
            {sourceSegment === 'demo'
              ? 'New Book Demo submissions from loksy.in/demo will show up here for callback.'
              : 'All enquiries are converted or no enquiries match your filters.'}
          </p>
        </div>
      ) : (
        <div className="enquiries-table-wrapper">
          <table className="enquiries-table">
            <thead>
              <tr>
                <th>Person Name</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Work Purpose</th>
                <th>Visited Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((enquiry) => (
                <tr key={enquiry.id}>
                  <td>
                    <strong>{enquiry.person_name || '—'}</strong>
                  </td>
                  <td>
                    <div className="phone-cell">
                      <span>{enquiry.phone || '—'}</span>
                      <div className="phone-actions">
                        <button
                          className="btn-icon btn-whatsapp"
                          onClick={() => handleWhatsApp(enquiry)}
                          title="WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                        <button
                          className="btn-icon btn-call"
                          onClick={() => handleCall(enquiry)}
                          title="Call"
                        >
                          <i className="fas fa-phone"></i>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="address-cell">
                      {(() => {
                        const billData = enquiry.bill_data || {};
                        const customerData = billData.customerData || {};
                        const address = customerData.address || '';
                        return address ? (
                          <span className="address-text" title={address}>
                            {address.length > 30 ? `${address.substring(0, 30)}...` : address}
                          </span>
                        ) : (
                          <span className="no-data">—</span>
                        );
                      })()}
                    </div>
                  </td>
                  <td>
                    <div className="work-purpose-cell">
                      {enquiry.work_purpose || <span className="no-data">—</span>}
                    </div>
                  </td>
                  <td>{formatDate(enquiry.visited_date)}</td>
                  <td>
                    {enquiry.total_amount > 0 ? (
                      <strong>{formatCurrency(enquiry.total_amount)}</strong>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>{getStatusBadge(enquiry.status)}</td>
                  <td>
                    <div className="remarks-cell">
                      {enquiry.remarks ? (
                        <span className="remarks-text" title={enquiry.remarks}>
                          {enquiry.remarks.length > 50
                            ? `${enquiry.remarks.substring(0, 50)}...`
                            : enquiry.remarks}
                        </span>
                      ) : (
                        <span className="no-remarks">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEdit(enquiry)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      {sourceSegment === 'old' && enquiry.status !== 'converted' && (
                        <button
                          className="btn-icon btn-convert"
                          onClick={() => handleConvert(enquiry)}
                          title="Convert to Business"
                        >
                          <i className="fas fa-exchange-alt"></i>
                        </button>
                      )}
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(enquiry)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingEnquiry && (
        <div className="edit-modal">
          <div className="edit-modal-backdrop" onClick={() => setEditingEnquiry(null)}></div>
          <div className="edit-modal-content">
            <h3>Edit Enquiry</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Person Name *</label>
                <input
                  type="text"
                  value={editForm.person_name}
                  onChange={(e) => handleEditChange('person_name', e.target.value)}
                  placeholder="Enter person name"
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => handleEditChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={editForm.address}
                  onChange={(e) => handleEditChange('address', e.target.value)}
                  placeholder="Enter customer address"
                  rows={3}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Work Purpose</label>
                <input
                  type="text"
                  value={editForm.work_purpose}
                  onChange={(e) => handleEditChange('work_purpose', e.target.value)}
                  placeholder="e.g., Admission, Passport, PAN Card, etc."
                />
              </div>

              <div className="form-group">
                <label>Visited Date</label>
                <input
                  type="date"
                  value={editForm.visited_date}
                  onChange={(e) => handleEditChange('visited_date', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Status *</label>
                <div className="status-options">
                  {STATUS_OPTIONS.map((status) => (
                    <label key={status.value} className="status-option">
                      <input
                        type="radio"
                        name="status"
                        value={status.value}
                        checked={editForm.status === status.value}
                        onChange={(e) => handleEditChange('status', e.target.value)}
                      />
                      <span
                        className="status-option-label"
                        style={{
                          borderColor: editForm.status === status.value ? status.color : '#d1d5db',
                          backgroundColor: editForm.status === status.value ? `${status.color}15` : '#fff',
                          color: editForm.status === status.value ? status.color : '#4b5563',
                        }}
                      >
                        <i className={status.icon}></i> {status.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Remarks / Updates</label>
                <textarea
                  value={editForm.remarks}
                  onChange={(e) => handleEditChange('remarks', e.target.value)}
                  placeholder="Enter any remarks, follow-up notes, or updates..."
                  rows={4}
                ></textarea>
                <small className="form-hint">
                  ⓘ Use this field to track follow-up conversations and updates.
                </small>
              </div>
            </div>

            <div className="edit-modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingEnquiry(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                {loading ? 'Updating...' : 'Update Enquiry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConvertModal && convertingEnquiry && (
        <div className="convert-modal">
          <div className="convert-modal-backdrop" onClick={() => setShowConvertModal(false)}></div>
          <div className="convert-modal-content">
            <h3>Convert Enquiry to Business</h3>
            <div className="convert-info">
              <p>
                Are you sure you want to convert this enquiry to business? The enquiry data will be
                pre-filled in the bill creation form.
              </p>
              <div className="convert-details">
                <div>
                  <strong>Person:</strong> {convertingEnquiry.person_name}
                </div>
                <div>
                  <strong>Phone:</strong> {convertingEnquiry.phone}
                </div>
                <div>
                  <strong>Work Purpose:</strong> {convertingEnquiry.work_purpose || 'N/A'}
                </div>
                {convertingEnquiry.total_amount > 0 && (
                  <div>
                    <strong>Estimated Amount:</strong> {formatCurrency(convertingEnquiry.total_amount)}
                  </div>
                )}
              </div>
            </div>
            <div className="convert-modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowConvertModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmConvert} disabled={loading}>
                {loading ? 'Converting...' : 'Convert to Business'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enquiries;

